"use client";

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useRouter, useParams } from "next/navigation";
import Sites from "src/constants/Sites";
import { useAppContext } from "@components/Provider";

import styles from "./AlgoliaSearch.module.css";
import "./AlgoliaSearch.css";

const AlgoliaSearch = (props) => {
  const router = useRouter();
  const params = useParams();

  const { siteId } = useAppContext()
  const { setIsOpen, classes = "", showSearch, isMobile = false, isTablet = false } = props;

  const [inputText, setInputText] = useState(params.searchIndex ? decodeURIComponent(params.search) : "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isInputFocussed, setIsInputFocussed] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(-1);

  const inputRef = useRef(null);

  useEffect(() => {
    if (showSearch) {
      // Focus on the input after the component becomes visible
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      // Clean up the timer if the component unmounts or if showSearch changes
      return () => clearTimeout(timer);
    }
  }, [showSearch]);

  // Ensure menu closes when component unmounts
  useEffect(() => {
    return () => {
      if (setIsOpen) {
        setIsOpen(false);
      }
    };
  }, [setIsOpen]);

  // Close menu when route changes (for Next.js App Router)
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (setIsOpen) {
        setIsOpen(false);
      }
    };

    // Listen for page unload/navigation
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [setIsOpen]);

  // Set search icon color based on site ID
  useEffect(() => {
    if (siteId) {
      const searchIconColor = siteId === Sites.EARLYBIRDBOOKS ? '#244090' : '#000000';
      document.documentElement.style.setProperty('--search-icon-color', searchIconColor);
    }
  }, [siteId]);

  const options = useMemo(() => {
    let opts = [];
    if (siteId === Sites.EARLYBIRDBOOKS || siteId === Sites.OPENROADMEDIA) {
      opts = opts.concat([
        { label: 'in Books', searchType: 'books' },
        { label: 'in Authors', searchType: 'authors' },
      ]);
    }
    if (siteId === Sites.EARLYBIRDBOOKS) {
      opts.push({ label: 'in Articles', searchType: 'articles' });
    }
    if (siteId === Sites.EARLYBIRDBOOKS || siteId === Sites.OPENROADMEDIA) {
      opts.push({ label: 'in Genres', searchType: 'genres' });
    }
    return opts;
  }, [siteId]);

  const handleSearch = (searchIndex, searchTerm) => {
    setShowDropdown(false);
    // Ensure the offcanvas menu closes when navigating to search results
    if (setIsOpen) {
      setIsOpen(false);
    }
    // Add a small delay to ensure the menu close is processed before navigation
    setTimeout(() => {
      router.push(`/search-results/${searchIndex}/${searchTerm}`);
    }, 100);
  };

  const handleQueryChange = (e) => {
    const input = e.target.value;
    // Hide suggestions dropdown if input is empty or is out of focus
    // Only show dropdown for earlybirdbooks (site ID 1) and openroadmedia (site ID 6)
    const shouldShowDropdown = input !== '' && 
                              input.length >= 3 && 
                              isInputFocussed && 
                              (siteId === Sites.EARLYBIRDBOOKS || siteId === Sites.OPENROADMEDIA);
    setShowDropdown(shouldShowDropdown);
    setInputText(input);
    setSelectedOptionIndex(-1);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const defaultIndex =
      siteId == Sites.EARLYBIRDBOOKS || siteId == Sites.OPENROADMEDIA
        ? "books"
        : "articles";
    let value = e.target[0]?.value?.trim();
    if (value !== "") {
      handleSearch(defaultIndex, value);
    }
    setShowDropdown(false);
    setIsOpen && setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    switch (event.key) {
      case 'ArrowDown':
        // Only handle arrow navigation for sites that support dropdown
        if (siteId === Sites.EARLYBIRDBOOKS || siteId === Sites.OPENROADMEDIA) {
          event.preventDefault();
          setSelectedOptionIndex((prevIndex) => (prevIndex + 1) % options.length);
        }
        break;
      case 'ArrowUp':
        // Only handle arrow navigation for sites that support dropdown
        if (siteId === Sites.EARLYBIRDBOOKS || siteId === Sites.OPENROADMEDIA) {
          event.preventDefault();
          setSelectedOptionIndex(
            (prevIndex) => (prevIndex - 1 + options.length) % options.length
          );
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedOptionIndex !== -1) {
          const option = options[selectedOptionIndex];
          handleSearch(option.searchType, inputText);
        } else if (
          siteId === Sites.EARLYBIRDBOOKS ||
          siteId === Sites.OPENROADMEDIA
        ) {
          handleSearch('books', inputText);
        } else {
          handleSearch('articles', inputText);
        }
        break;
    }
  };

  return (
    <div className={[
      styles["searchbox"],
      classes,
      isMobile || isTablet ? styles["searchboxMobile"] : ""
    ].join(" ")}>
      <div className={styles.inputWrapper}>
        <input
          ref={inputRef}
          className={styles['searchinput']}
          value={inputText}
          placeholder={'Search for books, author or genre'}
          onChange={handleQueryChange}
          onFocus={() => setIsInputFocussed(true)}
          onKeyDown={handleKeyDown}
        />
        <svg
          className={styles.searchIcon}
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
        </svg>
      </div>
      {showDropdown && (
        <div className={styles.searchDropdown}>
          {(siteId == Sites.EARLYBIRDBOOKS ||
            siteId == Sites.OPENROADMEDIA) && (
              <>
                <button style={{ background: selectedOptionIndex === 0 ? '#f5f5f5' : undefined }}
                  onClick={() => {
                    handleSearch("books", inputText);
                  }}
                >
                  {inputText} <span>in Books</span>
                </button>
                <button style={{ background: selectedOptionIndex === 1 ? '#f5f5f5' : undefined }}
                  onClick={() => {
                    handleSearch("authors", inputText);
                  }}
                >
                  {inputText} <span>in Authors</span>
                </button>
              </>
            )}
          {siteId == Sites.EARLYBIRDBOOKS && (
            <button style={{ background: selectedOptionIndex === 2 ? '#f5f5f5' : undefined }}
              onClick={() => {
                handleSearch("articles", inputText);
              }}
            >
              {inputText} <span>in Articles</span>
            </button>
          )}
          {(siteId == Sites.EARLYBIRDBOOKS ||
            siteId == Sites.OPENROADMEDIA) && (
              <button style={{ background: selectedOptionIndex === 3 ? '#f5f5f5' : undefined }}
                onClick={() => {
                  handleSearch("genres", inputText);
                }}
              >
                {inputText} <span>in Genres</span>
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default AlgoliaSearch;
